import {AfterViewInit, Component, Inject, ViewChild} from '@angular/core';
import {CapMeldungService} from './cap-meldung.service';
import {FlexGrid} from '@grapecity/wijmo.grid';
import {CollectionView, SortDescription} from '@grapecity/wijmo';
import {CapMeldung} from '../shared/domain/cap-meldung';
import { CapMeldungArea } from '../shared/domain/cap-meldung-area';
import { CapMeldungResource } from '../shared/domain/cap-meldung-resource';
import { CapMeldungParameter } from '../shared/domain/cap-meldung-parameter';
import {MeldungLanguageElement} from './cap-meldung-element';
import {
    DATEFORMAT_CULTURE,
    ENABLE_MANUAL_FEEDBACK_VALUE,
    WS_SERVICE_SCOPE_DEFAULT_VALUE,
    WS_SERVICE_VERSION_DEFAULT_URL
} from '../shared/tokens';
import {ImageResourceExtension} from '../shared/domain/image-resource_extension';

function nullToUndefined<T>(item: T | undefined | null): T | undefined {
    return item === null ? undefined : item;
}

@Component({
    selector: 'sim-cap-meldung',
    templateUrl: './cap-meldung.component.html'
})

export class CapMeldungComponent implements AfterViewInit {

    @ViewChild('detailTextGrid')
    protected detailTextGrid?: FlexGrid;

    readonly capMeldungCollectionView = new CollectionView<CapMeldung>();

    readonly bannerCollectionView = new CollectionView<CapMeldung>();

    readonly meldungStringTable = new Array<MeldungLanguageElement>();

    numberOfLanguages?: number;

    mapHasDrawings = false;

    connected = true;

    readonly endpointVersions = ['v1', 'v2'];
    readonly scopes = ['Public', 'Restricted', 'All', ''];

    private pendingRefreshGUI: ReturnType<typeof setTimeout> | null = null;

    private cleanupTimer?: ReturnType<typeof setInterval>;

    private inhibitNextSelectionChangeEvent = false;

    constructor(public service: CapMeldungService,
                @Inject(DATEFORMAT_CULTURE) private readonly dateCulture: string,
                @Inject(WS_SERVICE_VERSION_DEFAULT_URL) public endpointVersion: string,
                @Inject(WS_SERVICE_SCOPE_DEFAULT_VALUE) public scope: string,
                @Inject(ENABLE_MANUAL_FEEDBACK_VALUE) public enableFeedbackValue: boolean) {
        this.capMeldungCollectionView.sortDescriptions.push(new SortDescription('alert.sentDate', false));
        this.bannerCollectionView.sortDescriptions.push(new SortDescription('alert.sentDate', false));
    }

    ngAfterViewInit(): void {
        this.startConsuming();
    }

    private startConsuming(): void {
        this.consumeEvents();
        this.cleanupExpiredMessages();
    }

    private stopConsuming(): void {
        this.service.forceDisconnect();
        const { cleanupTimer } = this;
        if (cleanupTimer) {
            clearInterval(cleanupTimer);
        }

        // List of CAP messages (excluding BANNER)
        this.capMeldungCollectionView.beginUpdate();
        this.capMeldungCollectionView.sourceCollection.clear();
        this.capMeldungCollectionView.endUpdate();
        this.capMeldungCollectionView.refresh();

        // List of BANNER message
        this.bannerCollectionView.beginUpdate();
        this.bannerCollectionView.sourceCollection.clear();
        this.bannerCollectionView.endUpdate();
        this.bannerCollectionView.refresh();
    }

    private cleanupExpiredMessages(): void {
        // Go through capMeldungCollectionView/bannerCollectionView and remove expired messages
        this.cleanupTimer = setInterval(() => {
            this.removeExpiredMessagesFromCollectionView(this.capMeldungCollectionView);
            this.removeExpiredMessagesFromCollectionView(this.bannerCollectionView);
        }, 5000);
    }

    private removeExpiredMessagesFromCollectionView(collectionView: CollectionView<CapMeldung>): void {
        collectionView.beginUpdate();
        collectionView.items.forEach(message => {
            if (message.alert.info != null) {
                if (message.alert.info[0].expires != null) {
                    if (new Date() > (message.alert.info[0].expiresDate as Date)) {
                        console.log('Expire date reached on cap meldung: ', message);
                        this.removeMeldung(message, collectionView);
                    }
                }
            } else {
                // remove message without info (usually Cancel messages)
                const sentDate = new Date(message.alert.sent);
                sentDate.setMinutes(sentDate.getMinutes() + 5);
                if (new Date() > sentDate) {
                    console.log('Remove message without info part: ', message);
                    this.removeMeldung(message, collectionView);
                }
            }
        });
        collectionView.endUpdate();
    }

    toggleAutomaticRefresh(): void {
        this.connected = !this.connected;

        if (this.connected) {
            // Connecting to WS
            if (this.endpointVersion === 'v1') {
                this.scope = '';
            }
            this.service.setEndpoint(this.endpointVersion, this.scope);
            this.startConsuming();
        } else {
            // Disconnect from WS
            this.stopConsuming();
        }
    }

    private consumeEvents(): void {
        this.service
            .consumeEvents()
            .subscribe(
                (event) => {
                    console.log('Received cap meldung: ', event);
                    if (event != null) {
                        this.capMeldungCollectionView.beginUpdate();
                        this.bannerCollectionView.beginUpdate();
                        this.addCapMeldung(event);
                        this.capMeldungCollectionView.endUpdate();
                        this.bannerCollectionView.endUpdate();
                        this.capMeldungCollectionView.refresh();
                        this.bannerCollectionView.refresh();
                    }
                },
                error => {
                    console.log(`consuming failed: ${error}`);
                }
            );
    }

    private addCapMeldung(event: CapMeldung): void {
        if (event.alert.code && event.alert.code.includes('ALERTSWISS_BANNER')) {
            this.handleReceivedMessage(event, this.bannerCollectionView);
        } else {
            this.handleReceivedMessage(event, this.capMeldungCollectionView);
        }
    }

    private handleReceivedMessage(event: CapMeldung, collectionView: CollectionView<CapMeldung>): void {
        // check if message is already displayed
        const messageInView = collectionView.items.find(
            (item: CapMeldung) => item.alert.identifier === event.alert.identifier);
        if (messageInView === undefined) {
            this.addNewMessage(event, collectionView);
        }
    }

    private addNewMessage(event: CapMeldung, collectionView: CollectionView<CapMeldung>): void {
        collectionView.sourceCollection.push(event);

        if (this.service.activeAlertsHandled()) {
            if (event.alert.references != null) {
                const references: string[] = event.alert.references.split(' ');
                references.forEach(reference => {
                    const items: string[] = reference.split(',');
                    collectionView.items.forEach(message => {
                        if (message.alert.identifier === items[1]) {
                            console.log('Update on existing message, remove older one ');
                            this.removeMeldung(message, collectionView);
                        }
                    });
                });
            }
        }
    }

    private removeMeldung(event: CapMeldung, collectionView: CollectionView<CapMeldung>): void {
        if (collectionView.currentItem === event) {
            (collectionView as CollectionView<
                | CapMeldung
                | undefined
            >).currentItem = undefined;
        }
        collectionView.remove(event);
    }

    onMeldungSelectionChanged(event: MouseEvent): void {
        this.selectNewEntry(this.selectedCapMeldung, this.bannerCollectionView, this.selectedBannerMeldung);
    }

    onBannerSelectionChanged(event: MouseEvent): void {
        this.selectNewEntry(this.selectedBannerMeldung, this.capMeldungCollectionView, this.selectedCapMeldung);
    }

    private selectNewEntry(
        currentSelection:
            | CapMeldung
            | undefined
        ,
        otherCollectionView: CollectionView<CapMeldung>,
        otherSelection:
            | CapMeldung
            | undefined
        ,
    ): void {
        if (!this.inhibitNextSelectionChangeEvent) {
            // Unselect potential selection in other collectionView
            this.unselectEntryInOtherTableIfNeeded(otherCollectionView, otherSelection);

            this.meldungStringTable.length = 0;
            if (currentSelection != null && currentSelection.alert.info != null) {
                this.buildDataGrid(currentSelection);
                if (this.detailTextGrid != null) {
                    this.detailTextGrid.collectionView.refresh();
                } else {
                    // On first selection, detailTextGrid is not valid yet
                    setTimeout(() => {
                        this.refreshGUI();
                    }, 1000);
                }
            }
        }
    }

    private unselectEntryInOtherTableIfNeeded(
        collectionView: CollectionView<CapMeldung>,
        selection:
            | CapMeldung
            | undefined
        ,
    ): void {
        if (selection != null) {
            // unselecting the entry in the other table triggers a selectionChanged which needs to be inhibited
            this.inhibitNextSelectionChangeEvent = true;
            (collectionView as CollectionView<
                | CapMeldung
                | undefined
            >).currentItem = undefined;
        }
        this.inhibitNextSelectionChangeEvent = false;
    }

    public onLoadedRows(): void {
        if (this.pendingRefreshGUI) {
            clearTimeout(this.pendingRefreshGUI);
        }
        // throttle refreshGUI as onLoadedRows is called several times on first load
        this.pendingRefreshGUI = setTimeout(() => {
            this.refreshGUI();
        }, 800);
    }

    private refreshGUI(): void {
        if (this.detailTextGrid != null) {
            this.detailTextGrid.rows.forEach((rowItem) => {
                if (rowItem.dataItem.isSingleEntry) {
                    rowItem.allowMerging = true;
                }
            });

            this.detailTextGrid.autoSizeRows();
        }
    }

    public sendAckReceived(reference: string): void {
        this.service.sendAck(reference, true, 'ACK_RECEIVED');
    }

    public sendAckDelivered(reference: string): void {
        this.service.sendAck(reference, true, 'ACK_DELIVERED');
    }

    public sendAckReceivedError(reference: string): void {
        this.service.sendAck(reference, false, 'ACK_RECEIVED', 'Some error during receive processing');
    }

    public sendAckDeliveredError(reference: string): void {
        this.service.sendAck(reference, false, 'ACK_DELIVERED', 'Some error during delivery processing');
    }

    public sendStatistic(reference: string): void {
        this.service.sendRandomStatistic(reference);
    }

    public get selectedCapMeldung():
        | CapMeldung
        | undefined
    {
        if (this.capMeldungCollectionView === undefined) {
            return undefined;
        } else {
            return nullToUndefined(this.capMeldungCollectionView.currentItem);
        }
    }

    public get selectedBannerMeldung():
        | CapMeldung
        | undefined
    {
        if (this.bannerCollectionView === undefined) {
            return undefined;
        } else {
            return nullToUndefined(this.bannerCollectionView.currentItem);
        }
    }

    getIconString(meldung: CapMeldung): string {
        if (meldung.alert.info != null) {
            switch (meldung.alert.info[0].severity) {
                case 'Severe':
                    return 'alarm';
                case 'Moderate':
                    return 'warning';
                case 'Minor':
                    return 'info';
                case 'Unknown':
                    return 'test';
            }
        }
        return '';
    }

    private buildDataGrid(meldung: CapMeldung): void {
        if (meldung.alert.info != null) {
            this.numberOfLanguages = meldung.alert.info.length;

            const languages = new Array<string>();
            for (let i = 0; i < this.numberOfLanguages ; i++) {
                languages.push(meldung.alert.info[i].language as string);
            }

            this.insertMultiLangElement('Language', languages, false, false, false);
            this.insertSingleLangElement('<h4>Alert</h4>', '<h4>Alert</h4>', true);
            this.insertSingleLangElement('Identifier', meldung.alert.identifier);
            this.insertSingleLangElement('Sent', this.formatDateString(meldung.alert.sentDate as Date));

            this.insertSingleLangElement('Sender', meldung.alert.sender);
            this.insertSingleLangElement('Scope', meldung.alert.scope);

            this.insertSingleLangElement('Status', meldung.alert.status);
            this.insertSingleLangElement('MsgType', meldung.alert.msgType);
            if (meldung.alert.source != null) {
                this.insertSingleLangElement('Source', meldung.alert.source);
            }
            if (meldung.alert.restrictions != null) {
                this.insertSingleLangElement('Restrictions', meldung.alert.restrictions);
            }
            if (meldung.alert.addresses != null) {
                this.insertSingleLangElement('Addresses', meldung.alert.addresses);
            }
            if (meldung.alert.code != null) {
                this.insertSingleLangElement('Code', meldung.alert.code.toString());
            }
            if (meldung.alert.note != null) {
                this.insertSingleLangElement('Note', meldung.alert.note);
            }
            if (meldung.alert.references != null) {
                this.insertSingleLangElement('References', meldung.alert.references);
            }
            if (meldung.alert.incidents != null) {
                this.insertSingleLangElement('Incidents', meldung.alert.incidents);
            }

            this.insertSingleLangElement('RSAKey MD5', meldung.alert.rsaKeyMD5Checksum);

            this.insertSingleLangElement('<h4>Info</h4>', '<h4>Info</h4>', true);
            if (meldung.alert.info[0].onset != null) {
                const onset = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    onset.push(this.formatDateString(meldung.alert.info[i].onsetDate as Date));
                }
                this.insertMultiLangElement('Onset', onset, false, false, false);
            }

            if (meldung.alert.info[0].expires != null) {
                const expires = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    expires.push(this.formatDateString(meldung.alert.info[i].expiresDate as Date));
                }
                this.insertMultiLangElement('Expires', expires, false, false, false);
            }

            const categories = new Array<string>();
            for (let i = 0; i < this.numberOfLanguages ; i++) {
                categories.push(meldung.alert.info[i].category.toString());
            }
            this.insertMultiLangElement('Category', categories, false, false, false);

            const event = new Array<string>();
            for (let i = 0; i < this.numberOfLanguages ; i++) {
                event.push(meldung.alert.info[i].event);
            }
            this.insertMultiLangElement('Event', event, false, false, false);

            if (meldung.alert.info[0].responseType != null) {
                const responseType = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    responseType.push((meldung.alert.info[i].responseType as string[]).toString());
                }
                this.insertMultiLangElement('ResponseType', responseType, false, false, false);
            }

            const urgency = new Array<string>();
            for (let i = 0; i < this.numberOfLanguages ; i++) {
                urgency.push(meldung.alert.info[i].urgency);
            }
            this.insertMultiLangElement('Urgency', urgency, false, false, false);

            const severity = new Array<string>();
            for (let i = 0; i < this.numberOfLanguages ; i++) {
                severity.push(meldung.alert.info[i].severity);
            }
            this.insertMultiLangElement('Severity', severity, false, false, false);

            const certainty = new Array<string>();
            for (let i = 0; i < this.numberOfLanguages ; i++) {
                certainty.push(meldung.alert.info[i].certainty);
            }
            this.insertMultiLangElement('Certainty', certainty, false, false, false);

            if (meldung.alert.info[0].audience != null) {
                const audience = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    audience.push(meldung.alert.info[i].audience as string);
                }
                this.insertMultiLangElement('Audience', audience, false, false, false);
            }

            if (meldung.alert.info[0].eventCode != null) {
                const eventCode = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    const result = new Array<string>();
                    (meldung.alert.info[i].eventCode as CapMeldungParameter[]).forEach(entry => {
                        result.push(entry.valueName + ':' + entry.value + ' ');
                    });
                    eventCode.push(result.join(''));
                }
                this.insertMultiLangElement('EventCode', eventCode, false, false, false);
            }

            if (meldung.alert.info[0].effective != null) {
                const effective = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    effective.push(this.formatDateString(meldung.alert.info[i].effectiveDate as Date));
                }
                this.insertMultiLangElement('Effective', effective, false, false, false);
            }

            const senderName = new Array<string>();
            for (let i = 0; i < this.numberOfLanguages ; i++) {
                senderName.push(meldung.alert.info[i].senderName as string);
            }
            this.insertMultiLangElement('SenderName', senderName, false, false, false);

            if (meldung.alert.info[0].headline != null) {
                const headline = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    let content = this.stripHtml(meldung.alert.info[i].headline as string);
                    if (meldung.alert.info[i].autoTranslatedField) {
                        // check if field is translated
                        const autoTranslatedFields = meldung.alert.info[i].autoTranslatedField.join();
                        if (autoTranslatedFields.includes('headline')) {
                            content = '<span class="translated">' + content + '</span>';
                        }
                    }
                    headline.push(content);
                }
                this.insertMultiLangElement('Headline', headline, false, false, true);
            }

            if (meldung.alert.info[0].description != null) {
                const description = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    let content = this.stripHtml(meldung.alert.info[i].description as string);
                    content = content.split('\n\n').join('<br>');
                    if (meldung.alert.info[i].autoTranslatedSubtextField) {
                        meldung.alert.info[i].autoTranslatedSubtextField.forEach( subtext => {
                            const searchText = subtext.split('|');
                            if (searchText[1] === 'description') {
                                if (content.includes(searchText[2])) {
                                    content = content.replace(searchText[2], '<span class="translated">' + searchText[2] + '</span>');
                                }
                            }
                        });
                    }
                    description.push(content);
                }
                this.insertMultiLangElement('Description', description, false, false, true);
            }

            if (meldung.alert.info[0].instruction != null) {
                const instruction = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    let content = this.stripHtml(meldung.alert.info[i].instruction as string);
                    content = content.split('\n\n').join('<br>');
                    if (meldung.alert.info[i].autoTranslatedSubtextField) {
                        meldung.alert.info[i].autoTranslatedSubtextField.forEach( subtext => {
                            const searchText = subtext.split('|');
                            if (searchText[1] === 'instruction') {
                                if (content.includes(searchText[2])) {
                                    content = content.replace(searchText[2], '<span class="translated">' + searchText[2] + '</span>');
                                }
                            }
                        });
                    }
                    instruction.push(content);
                }
                this.insertMultiLangElement('Instruction', instruction, false, false, true);
            }

            if (meldung.alert.info[0].web != null) {
                const web = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    web.push(meldung.alert.info[i].web as string);
                }
                this.insertMultiLangElement('Web', web, true, false, false);
            }

            if (meldung.alert.info[0].webURL != null) {
                for (let j = 0; j < meldung.alert.info[0].webURL.length; j++) {
                    const contactURL = new Array<string>();
                    for (let i = 0; i < this.numberOfLanguages ; i++) {
                        let content = meldung.alert.info[i].webURL[j];
                        if (meldung.alert.info[i].autoTranslatedListField) {
                            meldung.alert.info[i].autoTranslatedListField.forEach( listItem => {
                                const searchText = listItem.split('|');
                                if (searchText[1] === 'WebUrl') {
                                    if (searchText[2] === j.toString()) {
                                        const elements = content.split('|');
                                        content = elements[0] + '|<span class="translated">' + this.stripHtml(elements[1]) + '</span>';
                                    }
                                }
                            });
                        }
                        contactURL.push(content);
                    }
                    this.insertMultiLangElement('ContactURL', contactURL, false, false, true);
                }
            }

            if (meldung.alert.info[0].contact != null) {
                const contact = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    let content = this.stripHtml(meldung.alert.info[i].contact as string);
                    if (meldung.alert.info[i].autoTranslatedSubtextField) {
                        meldung.alert.info[i].autoTranslatedSubtextField.forEach( subtext => {
                            const searchText = subtext.split('|');
                            if (searchText[1] === 'contact') {
                                if (content.includes(searchText[2])) {
                                    content = content.replace(searchText[2], '<span class="translated">' + searchText[2] + '</span>');
                                }
                            }
                        });
                    }
                    contact.push(content);
                }
                this.insertMultiLangElement('Contact', contact, false, false, true);
            }

            if (meldung.alert.info[0].resource != null) {
                for (let j = 0; j < meldung.alert.info[0].resource.length; j++) {
                    const result = new Array<string>();
                    const resultUri = new Array<string>();
                    const sha1 = new Array<string>();
                    const sha256 = new Array<string>();
                    const sha512 = new Array<string>();

                    for (let i = 0; i < this.numberOfLanguages ; i++) {
                        const entry = (meldung.alert.info[i].resource as CapMeldungResource[])[j];
                        let content = this.stripHtml(entry.resourceDesc);
                        const imageResource = content.split('\n');

                        if (meldung.alert.info[i].autoTranslatedListField) {
                            meldung.alert.info[i].autoTranslatedListField.forEach( listItem => {
                                const searchText = listItem.split('|');
                                if (searchText[1] === 'ImageResourceTitle') {
                                    if (searchText[2] === j.toString()) {
                                        imageResource[0] = '<span class="translated">' + imageResource[0] + '</span>';
                                    }
                                }
                                if (searchText[1] === 'ImageResourceDescription') {
                                    if (searchText[2] === j.toString()) {
                                        imageResource[1] = '<span class="translated">' + imageResource[1] + '</span>';
                                    }
                                }
                            });
                        }

                        content = imageResource.join('<br>');

                        result.push(content + '<br>' +
                            this.stripHtml(entry.mimeType) + ', ' +
                            (entry.size == null ? '' : entry.size + ', ' ) +
                            (entry.uri == null ? '' : this.stripHtml(entry.uri) + ', ' ) +
                            (entry.derefUri == null ? '' : this.stripHtml(entry.derefUri) + ', ' ) +
                            (entry.digest == null ? '' : 'Digest: ' + this.stripHtml(entry.digest)) + ' ');
                        resultUri.push(entry.uri == null ? '' : entry.uri);

                        if (meldung.alert.info[i].imageResourceExt) {
                            sha1.push(meldung.alert.info[i].imageResourceExt[j].sha1);
                            sha256.push(meldung.alert.info[i].imageResourceExt[j].sha256);
                            sha512.push(meldung.alert.info[i].imageResourceExt[j].sha512);
                        }
                    }

                    this.insertMultiLangElement('Resource', result, false, false, true);

                    if (sha1.length > 0) {
                        this.insertMultiLangElement('SHA-1', sha1, false, false, false);
                        this.insertMultiLangElement('SHA-256', sha256, false, false, false);
                        this.insertMultiLangElement('SHA-512', sha512, false, false, false);
                    }

                    this.insertMultiLangElement('Resource URI', resultUri, true, false, false);
                }
            }

            this.mapHasDrawings = false;

            if (meldung.alert.info[0].area != null) {
                const areaDesc = new Array<string>();
                const altitude = new Array<string>();
                const ceiling = new Array<string>();
                const geocode = new Array<string>();
                const polygonCount = new Array<string>();
                const circleCount = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    const resultAreaDesc = new Array<string>();
                    const resultAltitude = new Array<string>();
                    const resultCeiling = new Array<string>();
                    const resultGeocode = new Array<string>();
                    let j = 0;
                    const detail = meldung.alert.info[i];
                    const detailArea = detail.area as CapMeldungArea[];
                    detailArea.forEach(entry => {
                        let area = this.stripHtml(entry.areaDesc);

                        if (detail.autoTranslatedListField) {
                            detail.autoTranslatedListField.forEach( listItem => {
                                const searchText = listItem.split('|');
                                if (searchText[1] === 'areaDesc') {
                                    if (searchText[2] === j.toString()) {
                                        area = '<span class="translated">' + area + '</span>';
                                    }
                                }
                            });
                        }

                        resultAreaDesc.push(area);

                        j++;

                        if (entry.altitude != null) {
                            resultAltitude.push(entry.altitude.toString());
                        }
                        if (entry.ceiling != null) {
                            resultCeiling.push(entry.ceiling.toString());
                        }

                        if (entry.geocode != null) {
                            entry.geocode.forEach(geoEntry => {
                                resultGeocode.push(geoEntry.valueName + ':' + geoEntry.value + ' ');
                            });
                        }
                    });
                    if (detailArea[0].polygon != null) {
                        polygonCount.push(detailArea[0].polygon.length.toString());
                        this.mapHasDrawings = true;
                    }

                    if (detailArea[0].circle != null) {
                        circleCount.push(detailArea[0].circle.length.toString());
                        this.mapHasDrawings = true;
                    }

                    if (resultAreaDesc.length) {
                        areaDesc.push(resultAreaDesc.join('<br>'));
                    }
                    if (resultAltitude.length) {
                        altitude.push(resultAltitude.join(','));
                    }
                    if (resultCeiling.length) {
                        ceiling.push(resultCeiling.join(','));
                    }
                    if (resultGeocode.length) {
                        geocode.push(resultGeocode.join(''));
                    }
                }
                if (areaDesc.length) {
                    this.insertMultiLangElement('AreaDesc', areaDesc, false, false, true);
                }
                if (altitude.length) {
                    this.insertMultiLangElement('Altitude', altitude, false, false, false);
                }
                if (ceiling.length) {
                    this.insertMultiLangElement('Ceiling', ceiling, false, false, false);
                }
                if (geocode.length) {
                    this.insertMultiLangElement('Geocode', geocode, false, false, false);
                }
                if (circleCount.length) {
                    this.insertMultiLangElement('Circle count', circleCount, false, false, false);
                }
                if (polygonCount.length) {
                    this.insertMultiLangElement('Polygon count', polygonCount, false, false, false);
                }
            }

            if (meldung.alert.autoTranslatedField) {
                let maxLength = 0;
                meldung.alert.info.forEach( item => {
                    maxLength = Math.max( maxLength, item.autoTranslatedField ? item.autoTranslatedField.length : 0);
                });
                for (let j = 0; j < maxLength; j++) {
                    const autoTranslatedContent = new Array<string>();
                    for (let i = 0; i < this.numberOfLanguages ; i++) {
                        const entry = meldung.alert.info[i].autoTranslatedField ? ( meldung.alert.info[i].autoTranslatedField[j] ? meldung.alert.info[i].autoTranslatedField[j] : ' ') : ' ';
                        autoTranslatedContent.push(entry);
                    }
                    this.insertMultiLangElement('autoTranslatedField', autoTranslatedContent, false, false, false);
                }
            }

            if (meldung.alert.autoTranslatedListField) {
                let maxLength = 0;
                meldung.alert.info.forEach( item => {
                    maxLength = Math.max( maxLength, item.autoTranslatedListField ? item.autoTranslatedListField.length : 0);
                });
                for (let j = 0; j < maxLength; j++) {
                    const autoTranslatedContent = new Array<string>();
                    for (let i = 0; i < this.numberOfLanguages ; i++) {
                        const entry = meldung.alert.info[i].autoTranslatedListField ? ( meldung.alert.info[i].autoTranslatedListField[j] ? meldung.alert.info[i].autoTranslatedListField[j] : ' ') : ' ';
                        autoTranslatedContent.push(entry);
                    }
                    this.insertMultiLangElement('autoTranslatedListField', autoTranslatedContent, false, false, false);
                }
            }

            if (meldung.alert.autoTranslatedSubtextField) {
                let maxLength = 0;
                meldung.alert.info.forEach( item => {
                    maxLength = Math.max( maxLength, item.autoTranslatedSubtextField ? item.autoTranslatedSubtextField.length : 0);
                });
                for (let j = 0; j < maxLength; j++) {
                    const autoTranslatedContent = new Array<string>();
                    for (let i = 0; i < this.numberOfLanguages ; i++) {
                        const entry = meldung.alert.info[i].autoTranslatedSubtextField ? ( meldung.alert.info[i].autoTranslatedSubtextField[j] ? meldung.alert.info[i].autoTranslatedSubtextField[j] : ' ') : ' ';
                        autoTranslatedContent.push(entry);
                    }
                    this.insertMultiLangElement('autoTranslatedSubtextField', autoTranslatedContent, false, false, false);
                }
            }

            if (meldung.alert.info[0].parameter != null) {
                const parameter = new Array<string>();
                for (let i = 0; i < this.numberOfLanguages ; i++) {
                    const result = new Array<string>();
                    (meldung.alert.info[i].parameter as CapMeldungParameter[]).forEach(entry => {
                        result.push(entry.valueName + ':' + entry.value + ' ');
                    });
                    parameter.push(result.join(''));
                }
                this.insertMultiLangElement('Parameter', parameter, false, false, false);
            }
        }
    }

    private formatDateString(date: Date): string {
        return date.toLocaleDateString(this.dateCulture) + ' ' +
            date.toLocaleTimeString(this.dateCulture);
    }

    private insertSingleLangElement(feature: string, entry: string, noHtmlStripping: boolean = false): void {
        const entries = new Array<string>();
        for (let i = 0; i < (this.numberOfLanguages as number); i++) {
            entries.push(entry);
        }
        this.insertMultiLangElement(feature, entries, false, true, noHtmlStripping);
    }

    private insertMultiLangElement(feature: string, entries: string[], isURI: boolean, isSingleEntry: boolean, noHtmlStripping: boolean): void {

        if (entries.length > 0 && !noHtmlStripping) {
            entries = entries.map(element => this.stripHtml(element));
        }

        if (isURI) {
            entries = entries.map(uri => uri == null ? '' : '<a href="' + uri + '" target="_blank">' + uri + '</a>');

        }

        this.meldungStringTable.push(
            {
                feature,
                entries,
                isSingleEntry
            }
        );
    }

    private stripHtml(element: string): string {
        return element == null ? '' : element.replace(/<[^>]*>/g, '');
    }
}
